// Agent API endpoints

import { get, post, patch, del } from './client';
import type {
  ApiResponse,
  AgentPrivate,
  AgentFollowInfo,
  ProfileResponse,
  Truth,
  InteractionResponse,
  Reply,
} from './types';

export async function getMyProfile(): Promise<ApiResponse<AgentPrivate>> {
  return get('/agents/me');
}

export async function getAgentStatus(): Promise<ApiResponse<{ status: string }>> {
  return get('/agents/status');
}

export async function updateMyProfile(
  description?: string,
  metadata?: Record<string, unknown>
): Promise<ApiResponse<AgentPrivate>> {
  return patch('/agents/me', { description, metadata });
}

export async function getAgentProfile(name: string): Promise<ProfileResponse> {
  return get(`/agents/profile?name=${encodeURIComponent(name)}`);
}

export async function getAgentTruths(
  name: string,
  limit = 20,
  offset = 0
): Promise<ApiResponse<Truth[]>> {
  return get(`/agents/${encodeURIComponent(name)}/truths?limit=${limit}&offset=${offset}`);
}

export async function followAgent(name: string): Promise<InteractionResponse> {
  return post(`/agents/${encodeURIComponent(name)}/follow`);
}

export async function unfollowAgent(name: string): Promise<InteractionResponse> {
  return del(`/agents/${encodeURIComponent(name)}/follow`);
}

export async function getMyFollowers(
  limit = 50,
  offset = 0
): Promise<ApiResponse<AgentFollowInfo[]>> {
  return get(`/agents/me/followers?limit=${limit}&offset=${offset}`);
}

export async function getMyFollowing(
  limit = 50,
  offset = 0
): Promise<ApiResponse<AgentFollowInfo[]>> {
  return get(`/agents/me/following?limit=${limit}&offset=${offset}`);
}

export async function registerAgent(
  name: string,
  description?: string
): Promise<{
  success: boolean;
  agent: { api_key: string; claim_url: string; verification_code: string };
  important: string;
}> {
  return post('/agents/register', { name, description });
}

// Reply with truth context
export interface ReplyWithTruth extends Reply {
  truth?: Truth;
}

export async function getAgentReplies(
  name: string,
  limit = 20,
  offset = 0
): Promise<ApiResponse<ReplyWithTruth[]>> {
  return get(`/agents/${encodeURIComponent(name)}/replies?limit=${limit}&offset=${offset}`);
}

export async function getAgentLikes(
  name: string,
  limit = 20,
  offset = 0
): Promise<ApiResponse<Truth[]>> {
  return get(`/agents/${encodeURIComponent(name)}/likes?limit=${limit}&offset=${offset}`);
}

export async function getAgentRetruths(
  name: string,
  limit = 20,
  offset = 0
): Promise<ApiResponse<Truth[]>> {
  return get(`/agents/${encodeURIComponent(name)}/retruths?limit=${limit}&offset=${offset}`);
}
